import React from "react";
import { Link as RouterLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Typography } from "@mui/material";
import { ArticleOutlined } from "@mui/icons-material";
import color from "../../../Constants/colors";
import { NewsCategory, NewsItem } from "../../../Models/ambassador";
import { useDashboardColors } from "../../HomePage/dashboardColors";
import { EmptyState } from "../Common/AmbassadorUi";
import { ambassadorPaths } from "../Common/ambassadorNavigation";
import { formatDate } from "../Common/format";
import { useAmbassadorLabels } from "../Common/useAmbassadorLabels";

// Each category keeps its own badge color, as on the news page.
export const CATEGORY_COLORS: Record<NewsCategory, string> = {
  [NewsCategory.PRODUCTS]: "#e07a1f",
  [NewsCategory.SALES]: "#1f6f8b",
  [NewsCategory.EVENTS]: "#b4572e",
  [NewsCategory.PLATFORM]: "#4f5d8a",
};

/** Short list of the latest news, as shown on the ambassador dashboard. */
const NewsTeaserList: React.FC<{ news: NewsItem[] }> = ({ news }) => {
  const { t, i18n } = useTranslation("ambassador");
  const labels = useAmbassadorLabels();
  const { primary } = useDashboardColors();

  if (!news.length) {
    return (
      <EmptyState
        icon={<ArticleOutlined />}
        title={t("news.emptyTitle")}
        text={t("news.emptyText")}
      />
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      {news.map((item) => {
        const category = labels.newsCategory(item.category);
        return (
          <Box
            key={item.id}
            component={RouterLink}
            to={ambassadorPaths.newsItem(item.id)}
            sx={{
              display: "flex",
              gap: 1.5,
              py: 1.5,
              textDecoration: "none",
              borderBottom: `1px solid ${color.gray100}`,
              "&:last-of-type": { borderBottom: "none" },
              "&:hover .news-title": { textDecoration: "underline" },
            }}
          >
            <Box
              aria-hidden
              sx={{
                width: 40,
                height: 40,
                flexShrink: 0,
                borderRadius: "10px",
                backgroundColor: CATEGORY_COLORS[item.category],
                color: color.white,
                fontSize: 13,
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {category.slice(0, 2).toUpperCase()}
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography sx={{ fontSize: 12, color: color.gray400 }}>
                {formatDate(item.published_at, i18n.language)} · {category}
              </Typography>
              <Typography
                className="news-title"
                sx={{ fontSize: 14, fontWeight: 700, color: primary }}
              >
                {item.title}
              </Typography>
              {item.teaser && (
                <Typography
                  sx={{
                    fontSize: 13,
                    color: color.gray500,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {item.teaser}
                </Typography>
              )}
            </Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default NewsTeaserList;
